/**
 * useDocumentStamp — adapter hook between the DocumentStamp popover and
 * the existing `useUpdateDocument` mutation.
 *
 * The popover speaks the shared TS contract (DocumentCategory,
 * ClassOptionsValue). The server speaks `UpdateDocumentInput`. This hook
 * translates between them and exposes a couple of optimistic-friendly
 * setters.
 *
 * It's deliberately thin — section components are responsible for their
 * own value/onChange wiring; the hook just holds the mutation and
 * surfaces save status + the last error so the popover can render the
 * "save error" state.
 */

import { useCallback, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiContext, API_BASE, handleResponse } from "@/contexts/ApiContext";
import type { Document, DocumentLook, DocumentWithBlocks } from "@/lib/api";
import {
  DEFAULT_CLASS_FOR_CATEGORY,
  type ClassOptionsValue,
  type DocumentCategory,
  type DocumentClass,
} from "@/types/documentClass";

/**
 * Server payload — looser than `UpdateDocumentInput` so we can send the
 * documentclass-first fields (sides/titlePage/orientation/balancedColumns/
 * documentCategory/documentClass) before Team 2 finalises the type.
 *
 * Team 1's preamble emission consumes these from the persisted Document.
 */
export interface DocumentStampUpdate {
  documentCategory?: DocumentCategory;
  documentClass?: DocumentClass;
  paperSize?: ClassOptionsValue["paperSize"];
  fontSize?: ClassOptionsValue["fontSize"];
  columns?: ClassOptionsValue["columns"];
  balancedColumns?: boolean;
  sides?: ClassOptionsValue["sides"];
  titlePage?: boolean;
  orientation?: ClassOptionsValue["orientation"];
  /** The document theme (design-handoffs/2026-10-05-document-themes). */
  look?: DocumentLook;
  /** The document's language: a code, or "auto" (API #350). */
  language?: string;
  /** A publisher profile id (GET /api/publishers); "" goes back to Lilia's own layout (API #373). */
  publisherProfile?: string;
}

export interface UseDocumentStampReturn {
  /** True while a PATCH is in-flight. */
  saving: boolean;
  /** Last save error, if any. Cleared on next successful save. */
  saveError: Error | null;
  /** Trigger an update of any subset of class fields. Returns a promise so
   *  callers can roll back on error. */
  update: (patch: DocumentStampUpdate) => Promise<void>;
  /** Convenience: update the category and reset class to the canonical
   *  default. Used by the Type & class section. */
  updateCategory: (next: DocumentCategory) => Promise<void>;
  /** Convenience: replace the full ClassOptionsValue. Used by Class
   *  options grid. */
  updateOptions: (next: ClassOptionsValue) => Promise<void>;
  /** Manually clear the saved error (e.g. after the toast auto-dismisses). */
  clearError: () => void;
}

export function useDocumentStamp(documentId: string): UseDocumentStampReturn {
  const queryClient = useQueryClient();
  const { fetchWithAuth } = useApiContext();
  const [saveError, setSaveError] = useState<Error | null>(null);

  const mutation = useMutation({
    mutationFn: async (patch: DocumentStampUpdate) => {
      const response = await fetchWithAuth(`${API_BASE}/documents/${documentId}`, {
        method: "PUT",
        body: JSON.stringify(patch),
      });
      return handleResponse<Document | DocumentWithBlocks>(response);
    },
    onSuccess: () => {
      setSaveError(null);
      queryClient.invalidateQueries({ queryKey: ["document", documentId] });
      queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
    onError: (err: Error) => {
      setSaveError(err);
    },
  });

  const update = useCallback(
    async (patch: DocumentStampUpdate) => {
      await mutation.mutateAsync(patch);
    },
    [mutation],
  );

  const updateCategory = useCallback(
    async (next: DocumentCategory) => {
      await mutation.mutateAsync({
        documentCategory: next,
        documentClass: DEFAULT_CLASS_FOR_CATEGORY[next],
      });
    },
    [mutation],
  );

  const updateOptions = useCallback(
    async (next: ClassOptionsValue) => {
      await mutation.mutateAsync({
        paperSize: next.paperSize,
        fontSize: next.fontSize,
        columns: next.columns,
        balancedColumns: next.balancedColumns,
        sides: next.sides,
        titlePage: next.titlePage,
        orientation: next.orientation,
      });
    },
    [mutation],
  );

  const clearError = useCallback(() => setSaveError(null), []);

  return {
    saving: mutation.isPending,
    saveError,
    update,
    updateCategory,
    updateOptions,
    clearError,
  };
}
